import React, { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import AccessEventStatusLabel from '@/components/AccessEventStatusLabel/AccessEventStatusLabel';
import Button from '@/components/Button/Button';
import { modalContext } from '@/context/modal.context.js';
import useAccount from '@/hooks/useAccount';
import useLog from '@/hooks/useLog';
import useMixpanel from '@/hooks/useMixpanel';
import Access from '@/utils/api/odas/access';
import { accessEventHelpers, resourceHelpers } from '@/utils/helpers';
import t from '@/utils/Language/Referral-translations';
import classes from './UtilizationQuestion.module.scss';

const UtilizationQuestion = ({ accessEvent }) => {
  const { enqueueModal } = useContext(modalContext);
  const { account } = useAccount();
  const { track } = useMixpanel();
  const { log } = useLog();
  const [processing, setProcessing] = useState(false);
  const router = useRouter();

  const changeAccessEventStatus = (to) => {
    return Access.createOrUpdate({ resource: accessEvent.resource, status: to, direct: accessEvent.direct_access }).then((response) => {
      setProcessing(false);
      updateAccessEvent(response.data);
      return response.data;
    });
  };

  const openChangeStatusModal = () => {
    enqueueModal({
      name: 'ChangeAccessEventStatusModal',
      onUtilized: () => logAction('utilized'),
      onRejected: () => logAction('rejected'),
      onReset: () => logAction('planned')
    });
  };

  const logAction = (status) => {
    setProcessing(true);
    return changeAccessEventStatus(status).then((newAccessEvent) => {
      let eventHash = resourceHelpers.mixpanelEventHash(accessEvent.resource);
      switch (status) {
        case 'utilized':
          track('Utilized resource', eventHash);
          log({
            event: 'utilized resource',
            via: router.query.s || 'opportunity button',
            message: accessEvent.resource.slug,
            object_type: accessEvent.resource.resource_type,
            object_id: accessEvent.resource.id
          });
          enqueueModal({
            name: 'NewReviewModal',
            accessEvent: accessEvent,
            resource: accessEvent.resource,
            user: account.data
          });
          break;
        case 'rejected':
          track('Resource access rejected', eventHash);
          log({
            event: 'resource access rejected',
            via: router.query.s || 'opportunity button',
            message: accessEvent.resource.slug,
            object_type: accessEvent.resource.resource_type,
            object_id: accessEvent.resource.id
          });
          enqueueModal({
            name: 'RelatedResourcesModal',
            resetAccessEventStatus: () => {
              return changeAccessEventStatus('planned');
            },
            resource: accessEvent.resource,
            user: account.data
          });
          break;
      }
    });
  };

  const updateAccessEvent = (accessEvent) => {
    account.updateAccessEventCollection({ action: 'statusChange', accessEvent });
  };

  return (
    <>
      {!accessEventHelpers.utilizationQuestionAnswered(accessEvent) && (
        <div className={classes.utilizationQuestionContainer}>
          <p data-cy="text-utilization-question">{t('Did you use this service?')}</p>
          <Button
            dataCy="button-yes"
            type="default"
            disabled={processing}
            text={t('Yes')}
            icon={{ style: 'fas', font: 'check', position: 'left' }}
            onButtonClick={() => logAction('utilized')}
            additionalClasses="me-2"
          />
          <Button
            dataCy="button-no"
            type="default"
            disabled={processing}
            text={t('No')}
            icon={{ style: 'fas', font: 'times', position: 'left' }}
            additionalClasses={classes.darkerGrey}
            onButtonClick={() => logAction('rejected')}
          />
        </div>
      )}
      {accessEventHelpers.utilizationQuestionAnswered(accessEvent) && (
        <div className={classes.utilizationStatusContainer}>
          <AccessEventStatusLabel accessEvent={accessEvent} trackBySupporter={false} />
          <Button
            dataCy="accordion-item-change-status"
            type="link"
            disabled={processing}
            text={'(' + t('Change my status') + ')'}
            additionalClasses={classes.darkerGrey}
            onButtonClick={openChangeStatusModal}
          />
        </div>
      )}
    </>
  );
};

export default UtilizationQuestion;
